import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { DeleteConfirmDialog } from "../../components/DeleteConfirmDialog";
import { deleteMember, getMemberDetail } from "../../lib/adminApi";

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className={`mt-0.5 text-sm text-text ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export function AdminMemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    getMemberDetail(id)
      .then(setDetail)
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not load member.");
        navigate("/admin/members", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  async function confirmDelete() {
    setBusy(true);
    try {
      await deleteMember(id);
      toast.success("Member deleted.");
      navigate("/admin/members", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  if (loading || !detail) {
    return <div className="text-muted" data-testid="member-detail-loading">Loading…</div>;
  }
  const m = detail.member;
  const p = detail.profile;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-member-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/members" className="text-xs text-muted hover:text-gold" data-testid="back-to-members">
            ← Back to members
          </Link>
          <h1 className="mt-2 text-3xl font-semibold text-text">
            {p?.name || m.email || m.mobile_e164 || "Member"}
          </h1>
          <p className="mt-1 text-sm text-muted">Access status: <span className="text-gold">{m.access_status}</span></p>
        </div>
        <Button variant="destructive" onClick={() => setConfirmOpen(true)} data-testid="delete-member-btn">
          Delete member
        </Button>
      </div>

      <DeleteConfirmDialog
        open={confirmOpen}
        busy={busy}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={`Delete ${m.email || m.mobile_e164 || "member"}?`}
        description="This permanently removes the member, their financial profile, and every saved scenario / calculator report they own. Are you sure?"
        confirmLabel="Yes, delete member"
        testId="member-delete-dialog"
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <KV label="Email" value={m.email} />
        <KV label="Mobile" value={m.mobile_e164} mono />
        <KV label="Role" value={m.role} />
        <KV label="Joined" value={m.created_at ? new Date(m.created_at).toLocaleString() : "—"} />
        <KV label="Scenarios saved" value={detail.scenarios_count} />
        <KV label="Has profile" value={m.has_profile ? "Yes" : "No"} />
      </div>

      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="text-lg font-semibold">Financial profile</h3>
        {!p ? (
          <p className="mt-2 text-sm text-muted">This member hasn&apos;t saved a profile yet.</p>
        ) : p.error ? (
          <p className="mt-2 text-sm text-red-400">{p.error}</p>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <KV label="Name" value={p.name} />
            <KV label="Mobile" value={p.mobile} mono />
            <KV label="Income range" value={p.income_range} />
            <KV label="Approx. net worth (₹)" value={p.approx_net_worth?.toLocaleString?.("en-IN")} mono />
            <KV label="Consent given" value={p.consent_given ? "Yes" : "No"} />
            <KV label="Consent at" value={p.consent_at ? new Date(p.consent_at).toLocaleString() : "—"} />
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted">Short-term goals</div>
              {(p.short_term_goals || []).length === 0 ? (
                <div className="mt-1 text-sm text-muted">None</div>
              ) : (
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
                  {p.short_term_goals.map((g) => (
                    <li key={`st-${g.name}-${g.target_year}`}><span className="text-text">{g.name}</span> — ₹{g.target_amount?.toLocaleString?.("en-IN")} by {g.target_year}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-muted">Long-term goals</div>
              {(p.long_term_goals || []).length === 0 ? (
                <div className="mt-1 text-sm text-muted">None</div>
              ) : (
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
                  {p.long_term_goals.map((g) => (
                    <li key={`lt-${g.name}-${g.target_year}`}><span className="text-text">{g.name}</span> — ₹{g.target_amount?.toLocaleString?.("en-IN")} by {g.target_year}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
